import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';

const MOIS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

export default function BudgetPage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [budget, setBudget] = useState([]);
  const [loading, setLoading] = useState(true);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const printRef = useRef();

  const load = useCallback(async () => {
    try { const r = await api.get(`/comptabilite/budget?annee=${annee}`); setBudget(r.data); } catch {} finally { setLoading(false); }
  }, [annee]);

  useEffect(() => { load(); }, [load]);

  // Group by poste
  const postes = {};
  budget.forEach(b => {
    if (!postes[b.poste]) postes[b.poste] = {};
    postes[b.poste][b.mois] = b;
  });

  const totalPrevu = budget.reduce((s, b) => s + Number(b.montant_prevu || 0), 0);
  const totalRealise = budget.reduce((s, b) => s + Number(b.montant_realise || 0), 0);
  const ecart = totalRealise - totalPrevu;

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--gss-text-light)' }}>Chargement...</p>;

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div />
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`Budget & Prévisions — ${annee}`, printRef.current, tenant)}>Exporter PDF</button>
      </div>
      <div ref={printRef}>
      <div className="stats-grid stats-grid--3">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Budget prevu {annee}</span></div>
          <div className="stat-card-value">{totalPrevu.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Realise</span></div>
          <div className="stat-card-value" style={{ color: '#1565c0' }}>{totalRealise.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Ecart</span></div>
          <div className="stat-card-value" style={{ color: ecart <= 0 ? '#2e7d32' : '#DC2626' }}>{ecart.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA ({ecart <= 0 ? 'sous budget' : 'depassement'})</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Budget & Previsions — {annee}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="gss-btn" onClick={() => setAnnee(a => a - 1)}>&larr; {annee - 1}</button>
            <button className="gss-btn" onClick={() => setAnnee(a => a + 1)}>{annee + 1} &rarr;</button>
          </div>
        </div>

        {Object.keys(postes).length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucune donnee budgetaire pour {annee}</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Poste</th>
                  {MOIS.map((m, i) => <th key={i} style={{ fontSize: 11 }}>{m.substring(0, 3)}</th>)}
                  <th>Total prevu</th>
                  <th>Total realise</th>
                  <th>Ecart</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(postes).map(([poste, moisData]) => {
                  const prevuPoste = Object.values(moisData).reduce((s, b) => s + Number(b.montant_prevu || 0), 0);
                  const realisePoste = Object.values(moisData).reduce((s, b) => s + Number(b.montant_realise || 0), 0);
                  const ecartPoste = realisePoste - prevuPoste;
                  return (
                    <tr key={poste}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{poste}</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const d = moisData[i + 1];
                        return (
                          <td key={i} style={{ textAlign: 'right', fontSize: 11 }}>
                            {d ? (
                              <div>
                                <div style={{ color: '#888' }}>{Number(d.montant_prevu || 0).toLocaleString()}</div>
                                {Number(d.montant_realise || 0) > 0 && (
                                  <div style={{ fontWeight: 600, color: Number(d.montant_realise) > Number(d.montant_prevu) ? '#DC2626' : '#2e7d32' }}>
                                    {Number(d.montant_realise).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            ) : '-'}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{prevuPoste.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#1565c0' }}>{realisePoste.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: ecartPoste <= 0 ? '#2e7d32' : '#DC2626' }}>{ecartPoste.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #8B1A1A' }}>
                  <td>TOTAL</td>
                  <td colSpan={12}></td>
                  <td style={{ textAlign: 'right' }}>{totalPrevu.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', color: '#1565c0' }}>{totalRealise.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', color: ecart <= 0 ? '#2e7d32' : '#DC2626' }}>{ecart.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
